/**
 * Legal Gen 5 team randomizer.
 * Produces a full 6-Pokemon team with random species, moves, abilities,
 * natures, EVs, IVs, gender, and items - all legal in Generation 5.
 */

import type { PokemonSet, Team } from "./types";
import {
  getAllSpecies,
  getSpeciesAbilities,
  getAllNatures,
  getAllItems,
  getLearnset,
} from "../data/gen5";
import { MAX_TEAM_SIZE, MAX_EVS, MAX_IV, FORMAT } from "../config/constants";

/** Create a random Team with 6 legal Gen 5 Pokemon sets.
 * Each Pokemon must have at least 4 learnable moves so the engine can function.
 */
export async function randomizeTeam(): Promise<Team> {
  const allSpecies = getAllSpecies();
  const allNatures = getAllNatures();
  const allItems = getAllItems();
  const members: PokemonSet[] = [];

  for (let i = 0; i < MAX_TEAM_SIZE; i++) {
    const set = await randomizePokemon(allSpecies, allNatures, allItems, 4);
    members.push(set);
  }

  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: "Random Team",
    format: FORMAT,
    members,
    createdAt: now,
    updatedAt: now,
  };
}

/** Create a single random legal Pokemon set.
 * If a species has fewer than minMoves learnable moves, it is rejected and another species is picked.
 */
async function randomizePokemon(
  allSpecies: string[],
  allNatures: string[],
  allItems: string[],
  minMoves = 0,
): Promise<PokemonSet> {
  // Pick random species, but ensure it has enough learnable moves if required
  let species = pickRandom(allSpecies);
  let learnset = await getLearnset(species);
  let moveIds = learnset ? Object.keys(learnset) : [];
  if (minMoves > 0) {
    let attempts = 0;
    while (moveIds.length < minMoves && attempts < 50) {
      species = pickRandom(allSpecies);
      learnset = await getLearnset(species);
      moveIds = learnset ? Object.keys(learnset) : [];
      attempts++;
    }
  }

  // Pick random legal ability
  const abilities = getSpeciesAbilities(species);
  const ability = abilities.length > 0 ? pickRandom(abilities) : "";

  // Pick random nature
  const nature = pickRandom(allNatures);

  // Pick random item (optional, 50% chance)
  const item = Math.random() < 0.5 ? pickRandom(allItems) : undefined;

  // Pick random EVs (total <= 510, each <= 252)
  const evs = randomEvs();

  // Pick random IVs (0-31 each)
  const ivs = randomIvs();

  // Pick random gender (simplified - not checking actual gender ratios)
  const gender = pickRandom(["", "M", "F"]) as "" | "M" | "F";

  // Random shiny (low chance)
  const shiny = Math.random() < 0.0625; // 1/16 for fun, real games are 1/8192

  // Pick random moves from learnset
  const moves = pickRandomMoves(moveIds, 4);

  return {
    species,
    ability,
    nature,
    evs,
    ivs,
    moves,
    item,
    gender,
    shiny,
    level: 50,
    happiness: 255,
  };
}

/** Generate random EVs with total <= 510 and each stat <= 252. */
function randomEvs(): Record<string, number> {
  const stats = ["hp", "atk", "def", "spa", "spd", "spe"];
  const evs: Record<string, number> = {};
  let remaining = MAX_EVS;

  for (const stat of stats) {
    const max = Math.min(252, remaining);
    const val = Math.random() < 0.3 ? 0 : Math.floor(Math.random() * (max + 1));
    evs[stat] = val;
    remaining -= val;
    if (remaining <= 0) {
      evs[stat] = remaining < 0 ? evs[stat] + remaining : evs[stat];
      break;
    }
  }

  return evs;
}

/** Generate random IVs, each 0-31. */
function randomIvs(): Record<string, number> {
  return {
    hp: Math.floor(Math.random() * (MAX_IV + 1)),
    atk: Math.floor(Math.random() * (MAX_IV + 1)),
    def: Math.floor(Math.random() * (MAX_IV + 1)),
    spa: Math.floor(Math.random() * (MAX_IV + 1)),
    spd: Math.floor(Math.random() * (MAX_IV + 1)),
    spe: Math.floor(Math.random() * (MAX_IV + 1)),
  };
}

/** Pick up to N random moves from a list of move IDs. */
function pickRandomMoves(moveIds: string[], count: number): string[] {
  if (moveIds.length === 0) return [];
  const shuffled = [...moveIds].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, moveIds.length));
}

/** Pick a random element from an array. */
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
